import * as queryString from "query-string";
import {
  clampedColourCorrection,
  guessCalibrationMeasurements,
  resolveStoredCalibration,
} from "../../../app/shared/lib/calibration_core";

export class OpticalGymScreen {
  // Get a stored calibration for the current display, or null if none is valid.
  static getCalibration() {
    return resolveStoredCalibration(
      [],
      window.localStorage["opticalgym_calibration"],
    );
  }

  static getEffectiveCalibration() {
    const stored_calibration = OpticalGymScreen.getCalibration();
    let calibration = stored_calibration
      ? {
          ...stored_calibration,
          colour_correction: { ...stored_calibration.colour_correction },
        }
      : OpticalGymScreen.guessCalibration();
    if (stored_calibration) {
      calibration = OpticalGymScreen.override_from_query_params(calibration);
    }
    calibration = OpticalGymScreen.applyClinicAnaglyphPreferences(
      calibration,
      !stored_calibration,
    );
    return OpticalGymScreen.applyColourCorrectionBounds(calibration);
  }

  // Bring every colour-correction channel into the range the application
  // supports, filling in the defaults when the calibration carries none. The
  // bounds live in the shared calibration core so that what the wizard can
  // save, what storage will read back, and what an exercise renders with are
  // all the same range.
  static applyColourCorrectionBounds(calibration) {
    calibration.colour_correction = clampedColourCorrection(
      calibration.colour_correction,
    );
    return calibration;
  }

  static guessCalibration() {
    const guess = guessCalibrationMeasurements();
    let default_anaglyph_orientation = "cyan-red";
    if (window.app_config && window.app_config.default_anaglyph_orientation) {
      default_anaglyph_orientation = window.app_config.default_anaglyph_orientation;
    }
    const anaglyph_preferences = OpticalGymScreen.clinicAnaglyphPreferences();
    if (anaglyph_preferences.default_orientation) {
      default_anaglyph_orientation = anaglyph_preferences.default_orientation;
    }

    let calibration = {
      ...guess,
      anaglyph_orientation: default_anaglyph_orientation,
      colour_correction: {
        red_brightness: 0.75,
        blue_brightness: 0.75,
        green_brightness: 0.75,
      }
    };
    calibration = OpticalGymScreen.override_from_query_params(calibration);
    return calibration;
  }

  static clinicAnaglyphPreferences() {
    const query_params = queryString.parse(location.search);
    const supported_orientations = ["red-cyan", "cyan-red", "red-green", "green-red"];
    const default_orientation = supported_orientations.includes(
      query_params.clinic_default_anaglyph_orientation
    )
      ? query_params.clinic_default_anaglyph_orientation
      : null;
    const disallow_different_glasses =
      query_params.clinic_disallow_different_glasses === "true" ||
      query_params.clinic_disallow_different_glasses === "1";

    return { default_orientation, disallow_different_glasses };
  }

  static applyClinicAnaglyphPreferences(calibration, calibration_was_guessed = false) {
    const preferences = OpticalGymScreen.clinicAnaglyphPreferences();
    const query_params = queryString.parse(location.search);
    const orientation_was_overridden =
      "calibration_override_anaglyph_orientation" in query_params;
    if (
      preferences.default_orientation &&
      (
        preferences.disallow_different_glasses ||
        (calibration_was_guessed && !orientation_was_overridden)
      )
    ) {
      calibration.anaglyph_orientation = preferences.default_orientation;
    }
    return calibration;
  }

  static override_from_query_params(calibration) {
    // Override calibration from query params if specified.
    // Calibration can be overridden by passing in `calibration_override_<prop>`
    // For example '?calibration_override_distance_cm=500`
    let query_params = queryString.parse(location.search);
    for (var prop in calibration) {
      if ("calibration_override_" + prop in query_params) {
        let new_value = query_params["calibration_override_" + prop];
        if (typeof calibration[prop] == "number") {
          calibration[prop] = Number(new_value);
        } else if (typeof calibration[prop] == "boolean") {
          if (new_value == "true" || new_value == "1") {
            calibration[prop] = true;
          } else {
            calibration[prop] = false;
          }
        } else {
          calibration[prop] = new_value;
        }
      }
    }

    return calibration;
  }

  // Some stupid devices (iOS cough cough) don't have width and height
  // correctly defined when they are in landscape mode.
  // So always use the largest as width and the smallest as height.
  // We enforce portrait mode, so this should be fine.
  static screen_width() {
    return Math.max(screen.width, screen.height);
  }

  // TODO: Take into account devices that cannot go fullscreen.
  static screen_height() {
    return Math.min(screen.width, screen.height);
  }

  /**
   * Given an offset string, parse it, returning both the value and the unit
   */
  static parseOffset(offset_string, default_unit = '') {
    if (typeof offset_string == "number") {
      return {
        value: offset_string,
        unit: default_unit || 'px'
      };
    }

    offset_string = offset_string.trim();

    var rx = /(-?\d*\.?\d*)\s*(.*)/;
    var match = rx.exec(offset_string);
    if (!match[0] || !match[1]) {
      throw "Unable to parse " + offset_string;
    }
    var value = +match[1];
    var unit = "";

    // Trim any trailing 's' off the unit
    var raw_unit = match[2].replace(/s$/, "");

    // parse unit
    if (!raw_unit) {
      raw_unit = default_unit || "px";
    }
    if ([
      "px",
      "pixel",
      "píxel",
      "פיקסל",
      "بكسل",
      "بيكسل"
    ].includes(raw_unit)) {
      unit = "px";
    } else if ([
      "cm",
      "centimeter",
      "centímetro",
      "centimetro",
      "centimètre",
      "centímetro",
      "centimetre",
      "סנטימטר",
      'ס"מ',
      "سنتيمتر",
      "سم"
    ].includes(raw_unit)) {
      unit = "cm";
    } else if (      [
      "mm",
      "millimeter",
      "millimètre",
      "milímetro",
      "milimetro",
      "מילימטר",
      'מ"מ',
      "مليمتر",
      "مم"
    ].includes(raw_unit)) {
      unit = "cm";
      value = value / 10;
    } else if (
      [
        "Δ",
        "prism diopter",
        "prism",
        "prisme",
        "prism-diopter",
        "dioptría de prisma",
        "dioptria de prisma",
        "dioptría prismática",
        "dioptria prismática",
        "dioptria prismatica",
        "dioptria de prisma",
        "דיופטרת פריזמטית",
        "דיופטריה",
        "פריזמה",
        "ديوبتر منشوري",
        "منشور",
        "ديوبتر",
        "درجة منشورية",
        "dioptria",
        "dioptría",
        "prisma",
        "prisme",
        "pdpt",
        "prdpt",
        "PD",
        "pd"
      ].includes(raw_unit)
    ) {
      unit = "pdpt"; // Prism diopter
    } else if (
      [
        "arcsecond",
        "seconde d'arc",
        "arc sec",
        "segundo de arco",
        "arcosegundo",
        "arc second",
        "arcsec",
        "arcseg",
        "asec",
        "as",
        "″",
        '"',
        "''",
        "second",
        "seconde",
        "segundo",
        "segunda",
        "שניית קשת",
        "seg de arco",
        "ثانية قوسية",
        "ثانية",
        "ث",
        "שנ׳",       // Hebrew abbreviation for שנייה, used for arcseconds
        "ثا"         // Arabic shorthand in some scientific texts for arcseconds
      ].includes(raw_unit)
    ) {
      unit = "arcsecond";
    } else if (
      [
        "arcminute",
        "arcmin",
        "amin",
        "am",
        "′",
        "'",
        "minute d'arc",
        "minuto de arco",
        "דקת קשת",
        "دقيقة قوسية",
        "دقيقة",
        "د",
        "ד׳",        // Hebrew abbreviation for דקה used for arcminutes
        "دق"         // Arabic short form for دقيقة قوسية (seen in educational texts)
      ].includes(raw_unit)
    ) {
      unit = "arcsecond";
      value = value * 60;
    } else if ([
      "degree",
      "deg",
      "°",
      "degré",
      "degré d'arc",
      "מעלה",
      "grado",
      "grados",
      "grau",
      "graus",
      "grau de arco",
      "grado de arco",
      "درجة",
      "°ق",
      "درجات",
      "درجه",
      "מ׳",         // Hebrew shorthand for מעלה, sometimes used
      "°م"          // Arabic shorthand variant for "degree" (not common but used)
    ].includes(raw_unit)) {
      unit = "arcsecond";
      value = value * 3600;
    } else {
      throw "Unable to parse " + offset_string;
    }

    return {
      value: value,
      unit: unit
    };
  }
}

if (typeof window !== "undefined") {
  window.OpticalGymScreen = OpticalGymScreen;
}
